import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Slider from 'react-slick';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';

import { useTranslate } from '../../../../../../i18n/useTranslationModules';
import { UploadFile } from '../../../../../../connection/uploads';
import { InsertVideo, UpdateVideo } from '../../../../../../connection/videos';
import { updateHomepageModule } from '../../../../../../store/actions/admin/create-event';
import { GetDefaultVideo } from '../../../../../../store/utils/create-event';
import { TemplateClassNames, VideosLayoutTypesBold, VideosLayoutTypesClassic, VideosLayoutTypesFun } from '../../../../../../types/template-layouts';
import {
	ColorKeys,
	VideoConvertStatus,
	CreateVideo,
	HomepageVideo,
	TextOverrides,
	ContentEditors,
} from '../../../../../../types/working-model';
import { getDefaultModuleAlignment, getTextValue, updateTranslateKey } from '../../../../../../utils/utils';
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import ImageEditor from '../../../../../general-ui/image-editor/image-editor';
import LargeButton from '../../../../../general-ui/button/large-button';
import Switch from '../../../../../general-ui/switch/switch';
import TextInput from '../../../../../general-ui/text-input/text';
import ModalComponent from '../../../../../general-ui/modal/modal';
import WaitingIndicator from '../../../../../general-ui/waiting-indicator/waiting-indicator';
import { MULTI_SLIDER_SETTINGS, SINGLE_SLIDER_SETTINGS, PrevArrow, NextArrow } from '../../../../../live-event/modules/video/videos';
import '../../../../../../scss/live-event/base/modules/videos.scss';
import './videos.scss';
import { createUploadJob, getChannelUpVideos } from '../../../../../../store/actions/admin/videos';
import CustomHTML from '../../../../../general-ui/edit-html/edit-html';
import { showAlert } from '../../../../../general-ui/alert/alert-service';
import { getStylingOverrides } from '../../../../../live-event/utils';
import Controls from '../../../session/editor/moduleControls/controls';
import { EContentColorOverrideNames } from '../../../../../general-ui/edit-section-colors/edit-section-colors-modal';
import EditableTextSlim from '../../../../../general-ui/editable-text/editable-text-slim';
import { BasicLangingModuleProps } from '../editor-main';
import Checkbox from '../../../../../general-ui/checkbox/checkbox';
import EditableTextDraftQuill from '../../../../../general-ui/editable-text/editable-text-draft-quill';
import { useTypedSelector } from '../../../../../../store/reducers/use-typed-selector';
import FileInput from '../../../../../../utils/file-input';
import useTranslationRoute from '../../../../../live-event/hooks/use-translation-route';
const Video = React.lazy(() => import('./video'));

const EditorVideo: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const namespace = useTranslationRoute();
	const { t } = useTranslate(namespace);
	const { token, user } = useTypedSelector(state => state.AuthReducer);
	const { videos, loadingVideos } = useTypedSelector(state => state.VideosReducer);
	const contentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);

	const [workingVideo, setWorkingVideo] = useState<
		CreateVideo | HomepageVideo | null
	>(null);
	const [addVideoType, setAddVideoType] = useState<HomepageVideo['type']>('recorded');
	const [workingSelectedVideos, setWorkingSelectedVideos] = useState<number[]>([]);
	const [workingVideoIndex, setWorkingVideoIndex] = useState<number | null>(null);
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	// const [inputError, setInputError] = useState('');
	const [uploading, setUploading] = useState(false);
	const [editingImage, setEditingImage] = useState(false);
	const [resizedImage, setResizedImage] = useState<File | null>(null);
	const replaceImageInput = useRef<HTMLInputElement | null>(null);
	const [confirmDelete, setConfirmDelete] = useState<HomepageVideo | null>(
		null
	);
	const [selectingType, setSelectingType] = useState(false);
	const [visibleVideos, setVisibleVideos] = useState<HomepageVideo[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const mounted = useRef(false);

	const dispatch = useDispatch();

	const content = page_module?.content;

	const layout_type = content?.layout_type || "standard";
	const styling_overrides = page_module.styling_overrides;
	const stylingOverrides = getStylingOverrides(styling_overrides);

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							language,
							baseLanguage,
						}),
					},
					...(key === 'description' && { is_edited: true }),
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					is_edited: true,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	function openSelectVideos() {
		setModalOpen(true);
		setWorkingSelectedVideos(page_module?.content_modules ?? []);
	}

	function closeModal() {
		setWorkingSelectedVideos(page_module?.content_modules ?? []);
		setWorkingVideoIndex(null);
		setAddVideoType('uploaded');
		setSelectingType(false);
		// delay resetting this state as to not show changing state as modal closes
		setTimeout(() => setWorkingVideo(null), 250);
		setResizedImage(null);
		setModalOpen(false);
	}

	function handleCancel() {
		// if editing thumbnail from the edit icon inside the modal, dont close the whole modal
		// just go back to selecting videos
		if (
			isSelectVideoView(addVideoType) &&
			workingVideo?.use_thumbnail &&
			workingVideoIndex === null
		) return setWorkingVideo(null);

		setWorkingSelectedVideos(page_module?.content_modules ?? []);
		setWorkingVideoIndex(null);
		setAddVideoType('uploaded');
		setSelectingType(false);
		// delay resetting this state as to not show changing state as modal closes
		setTimeout(() => setWorkingVideo(null), 250);
		setResizedImage(null);
		setModalOpen(false);
	}

	// function checkUrl() {
	// 	if (!validateUrl(workingVideo!.source as string)) {
	// 		setInputError(
	// 			`The url "${workingVideo!.source
	// 			}" does not appear to be valid. Please enter a valid url.`
	// 		);
	// 		return;
	// 	} else {
	// 		setInputError('');
	// 	}
	// }

	// function handleUrlChange({
	// 	target: { value },
	// }: React.ChangeEvent<HTMLInputElement>) {
	// 	setInputError('');
	// 	setWorkingVideo((video: CreateVideo | HomepageVideo | null) => {
	// 		if (!video) { return null; }

	// 		return {
	// 			...video,
	// 			source: value.trim(),
	// 		};
	// 	});
	// }

	function handleSwitch(value: string, isOn: boolean) {
		setWorkingVideo((video: CreateVideo | HomepageVideo | null) => {
			if (!video) { return null; }

			return {
				...video,
				use_thumbnail: isOn,
			};
		});
	}

	async function handleFile(file: File | FileList) {
		if (!user || !token) return;
		const _file = file as File;
		setUploading(true);

		const url = await UploadFile(user, token, _file);

		setWorkingVideo((video: CreateVideo | HomepageVideo | null) => {
			if (!video) { return null; }

			return {
				...video,
				original_image: url,
			};
		});

		setEditingImage(true);
		setUploading(false);
	}

	function editImage() {
		setEditingImage(true);
	}

	function cancelEditingImage() {
		setEditingImage(false);
	}

	function removeImage() {
		setWorkingVideo((video: CreateVideo | HomepageVideo | null) => {
			if (!video) { return null; }

			return {
				...video,
				image: '',
				original_image: '',
			};
		});
	}

	function pickNewPhoto() {
		replaceImageInput.current?.click();
	}

	async function handleNewPhoto(e: React.ChangeEvent<HTMLInputElement>) {
		if (!user || !token) return;
		const file = e.target.files?.[0];
		if (file) {
			const uploaded = await UploadFile(user, token, file);
			setWorkingVideo((video: CreateVideo | HomepageVideo | null) => {
				if (!video) { return null; }

				return {
					...video,
					image: uploaded,
					original_image: uploaded,
				};
			});
		}
	}

	function handleVideoEmbed(code: string) {
		setWorkingVideo(video => {
			if (!video) return null;

			return {
				...video,
				source: code
			};
		});
	}

	function handleVideoUrl({ target: { value } }: React.ChangeEvent<HTMLInputElement>) {
		setWorkingVideo(video => {
			if (!video) return null;

			return {
				...video,
				source: value
			};
		});
	}

	function setSelectedType(type: "uploaded" | "embed" | "url" | "recorded") {
		return () => {
			if (isSelectVideoView(type)) {
				setWorkingVideo(null);
			} else {
				setWorkingVideo({
					...GetDefaultVideo(),
					type
				});
			}
			setAddVideoType(type);
			setSelectingType(false);
		};
	}

	function handleSelectVideo(video: HomepageVideo) {
		return () => {
			setWorkingSelectedVideos(prev => {
				if (!prev) return [video.video];
				if (prev.includes(video.video)) return prev.filter((_video: number) => _video !== video.video);
				return [...prev, video.video];
			});
		};
	}

	function handleEditThumbnail(video: HomepageVideo) {
		return (e: any) => {
			e.stopPropagation();
			if (!video) return null;
			setWorkingVideo({ ...video, use_thumbnail: true });
		};
	}

	const handleSearch = useCallback(({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(value);
	}, []);


	function clickUpload() {
		if (!user || !token) return;
		dispatch(createUploadJob(() => {
			if (mounted.current) {
				dispatch(getChannelUpVideos(token, user.active_channel));
				showAlert({
					message: "Video Uploaded",
					description: "Your video has finished uploading!",
					duration: 5000
				});
			}
		}, () => {
			showAlert({
				message: "Video Uploading",
				description: "Your video is uploading. You can view its progress at the bottom right of the screen.",
				duration: 5000
			});
		}));
	}

	async function doneEditingImage() {
		if (!user || !token) return;
		if (resizedImage) {
			setUploading(true);
			const uploaded = await UploadFile(user, token, resizedImage);
			setWorkingVideo((video: CreateVideo | HomepageVideo | null) => {
				if (!video) { return null; }

				return {
					...video,
					image: uploaded,
				};
			});
		}
		setUploading(false);
		setEditingImage(false);
	}

	const handleEditingImage = useCallback(() => { return; }, []);

	useEffect(() => {
		setWorkingSelectedVideos(page_module.content_modules ?? []);
	}, [page_module.content_modules]);

	useEffect(() => {
		if (token && user && (modalOpen || !visibleVideos.length)) {
			dispatch(getChannelUpVideos(token, user.active_channel));
		}
	}, [dispatch, modalOpen, token, user, visibleVideos.length]);

	useEffect(() => {
		if (!searchTerm) {
			setVisibleVideos(videos);
		} else {
			setVisibleVideos(videos.filter(video => {
				return video.name.toLowerCase().includes(searchTerm.toLowerCase());
			}));
		}
	}, [videos, searchTerm]);

	useEffect(() => {
		mounted.current = true;

		return () => {
			mounted.current = false;
		};
	}, []);

	async function doneAddingVideo() {
		if (!user || !token) return;
		try {
			const fullVideos = videos?.filter(video => workingSelectedVideos?.includes(video.video));

			if (workingVideo) {
				// update a video with new thumbnail
				let video;
				if ('video' in workingVideo) {
					await UpdateVideo(token, workingVideo as HomepageVideo);
					video = workingVideo;
				} else {
					const newVideo = await InsertVideo(token, workingVideo);
					video = newVideo;
				}
				// if editing thumbnail of the recorded/uploaded videos, 
				if ((isSelectVideoView(addVideoType)) && workingVideo.use_thumbnail && workingVideoIndex === null) {
					// get all videos again and go back to selecting videos from here
					dispatch(getChannelUpVideos(token, user.active_channel));
					return setWorkingVideo(null);
				}

				const contentModules = [...(workingSelectedVideos ?? [])];
				const modules = [...(fullVideos ?? [])];

				if (workingVideoIndex !== null) {
					contentModules[workingVideoIndex] = video.video;
					modules[workingVideoIndex] = video;
				} else {
					contentModules.push(video.video);
					modules.push(video);

				}

				dispatch(
					updateHomepageModule({
						...page_module,
						is_edited: true,
						content_modules: contentModules,
						modules: modules,
					})
				);
			} else {
				// update module with selected videos

				dispatch(
					updateHomepageModule({
						...page_module,
						is_edited: true,
						content_modules: fullVideos.map(video => video.video),
						modules: fullVideos,
					})
				);
			}
			setTimeout(() => setWorkingVideo(null), 250);
			setResizedImage(null);
			setWorkingVideoIndex(null);
			setModalOpen(false);
		} catch (e) {
			console.error(e);
		}
	}

	const selectedVideos: HomepageVideo[] = useMemo(() => {
		if (page_module.modules) {
			return page_module.modules;
		} else {
			return [];
		}
	}, [page_module.modules]);

	const imageName = useMemo(() => {
		if (workingVideo?.original_image) {
			const parts = workingVideo.original_image.split('/');
			return parts[parts.length - 1];
		} else {
			return '';
		}
	}, [workingVideo?.original_image]);

	const sliderSettings = useMemo(() => {
		const isVideoListPreview =
			layout_type === VideosLayoutTypesBold.ltr ||
			layout_type === VideosLayoutTypesClassic.ltr ||
			layout_type === VideosLayoutTypesFun.ltr;

		return isVideoListPreview
			? { ...MULTI_SLIDER_SETTINGS }
			: { ...SINGLE_SLIDER_SETTINGS };
	}, [layout_type]);

	function editVideo(video: HomepageVideo, idx: number) {
		setResizedImage(null);
		setWorkingVideoIndex(idx);
		setWorkingVideo({ ...video, use_thumbnail: true });
		setModalOpen(true);
	}

	function checkDeleteVideo(video: HomepageVideo) {
		setConfirmDelete(video);
	}

	function removeVideo() {
		if (!confirmDelete) return;
		dispatch(
			updateHomepageModule({
				...page_module,
				is_edited: true,
				content_modules: page_module.content_modules?.filter(
					(module: number) => module !== confirmDelete.video
				),
				modules: page_module.modules?.filter(
					(module: HomepageVideo) =>
						module.video !== confirmDelete.video
				),
			})
		);
		setConfirmDelete(null);
	}

	const renderVideoSelect = useMemo(() => {
		switch (addVideoType) {
			case 'uploaded':
			case 'recorded': {
				// if editing thumbnail, dont return anything here
				return workingVideo?.use_thumbnail ? null : (
					<>
						<div className="recorded-header">
							<TextInput
								onChange={handleSearch}
								placeholder={'Search video...'}
								icon={ICONS.SEARCH}
								size={'small'}
							/>
							<span>or</span>
							<button className="small" onClick={clickUpload}>Upload video</button>
						</div>
						<div className="recorded-videos">
							{loadingVideos ? (
								<WaitingIndicator />
							) : (
								<>
									{visibleVideos.map(video => {
										const isSelected = workingSelectedVideos?.includes(video.video);
										return (
											<div key={video.video} className={classNames("recorded-video", { selected: isSelected })}>
												<div onClick={handleSelectVideo(video)} style={{ backgroundImage: video.image ? `url(${video.image})` : 'none' }} className="video-banner">
													<AnimatePresence>
														{video.convert_status === VideoConvertStatus.progressing && (
															<motion.div
																key={video.video}
																initial={{ opacity: 0 }}
																animate={{ opacity: 1 }}
																exit={{ opacity: 0 }}
																className="processing-overlay">
																<span>Your upload is currently processing.<br /><br />Progress: {video.convert_progress ?? 0}%</span>
															</motion.div>
														)}
													</AnimatePresence>
													<div className="video-options">
														<Checkbox
															onChange={handleSelectVideo(video)}
															value={'selectVideo'}
															checked={isSelected ?? false}
															size={20}
															color={isSelected ? COLORS.CYAN : COLORS.WHITE}
															backgroundColor={isSelected ? COLORS.WHITE : undefined}
															selectedIconName={ICONS.CHECKBOX_BORDERLESS}
															unselectedIconName={isSelected ? ICONS.OFF : undefined}
														/>
														<button className="no-style edit-thumbnail" onClick={handleEditThumbnail(video)}>
															<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={20} />
														</button>

													</div>
												</div>
												<label>{video.name}</label>
												{video.duration && <span className="duration">{new Date(video.duration * 1000).toISOString().substr(11, 8)}</span>}
											</div>
										);
									})}
								</>
							)}
						</div>
					</>
				);
			}
			case 'url': {
				if (!workingVideo) return null;
				return (
					<TextInput
						placeholder="Enter video URL"
						label={"Add video URL"}
						onChange={handleVideoUrl}
						style={{ marginTop: 16 }}
						defaultValue={workingVideo.source ?? ''}
					/>
				);
			}
			case 'embed': {
				if (!workingVideo) return null;
				return (
					<div className="video-embed-code">
						<label>Embed Code</label>
						<CustomHTML
							onChange={handleVideoEmbed}
							defaultValue={workingVideo.source ?? ''}
						/>
					</div>
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [workingVideo, visibleVideos, loadingVideos, handleSearch, workingSelectedVideos, videos]);

	const selectedWorkingType = useMemo(() => {
		if (!addVideoType) return null;
		switch (addVideoType) {
			case 'embed':
				return <EmbedIframe />;
			case 'recorded':
			case 'uploaded':
				return <AddVideo />;
			case 'url':
				return <LinkVideo />;
			default:
				return (<></>);
		}

	}, [addVideoType]);

	const { custom_heading, description, title } = getTextValue(content, language);
	const defaultCustomHeadingColor = template === TemplateClassNames.O2
		? ColorKeys.bodyTextColor
		: ColorKeys.secondaryAccentColor;

	const closeConfirmModal = useCallback(() => {
		setConfirmDelete(null);
	}, []);

	const modalTitle = useMemo(() => {
		if (editingImage) return 'Edit Photo';
		if (workingVideoIndex || (isSelectVideoView(addVideoType) && workingVideo?.use_thumbnail)) return 'Edit Thumbnail';
		return 'Add Video';
	}, [addVideoType, editingImage, workingVideo?.use_thumbnail, workingVideoIndex]);

	return page_module.is_on ? (
		<section
			ref={ref}
			className={classNames(
				"editor-wrapper videos",
				`videos-${layout_type}`,
				stylingOverrides.backgroundColor,
				template
			)}
		>

			<Controls
				openEditModal={() => console.log('edit')}
				openContentModal={openSelectVideos}
				module={page_module}
				contentButtonText="Add Videos"
				editableContent={{
					jsx: <p>placeholder</p>,
					onDone: () => ('placeholder')
				}}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className="module-container" style={stylingOverrides.backgroundImg}>
				<div className="video-title-container">
					<EditableTextSlim
						className="video-custom-heading"
						tagType="p"
						value={t(`${namespace}:videos.${page_module.id}.heading`, t(`${namespace}:videos.heading`, custom_heading))}
						onChange={handleTextChange("custom_heading")}
						elementName={EContentColorOverrideNames.custom_heading}
						defaultColor={defaultCustomHeadingColor}
						colorOverride={styling_overrides?.content?.custom_heading?.color}
						onOverrideChange={handleStyleOverrideChange("custom_heading")}
					/>
					<EditableTextSlim
						className="video-title"
						tagType="h2"
						value={t(`${namespace}:videos.${page_module.id}.title`, t(`${namespace}:videos.title`, title))}
						onChange={handleTextChange("title")}
						elementName={EContentColorOverrideNames.title}
						defaultColor={ColorKeys.bodyTextColor}
						colorOverride={styling_overrides?.content?.title?.color}
						onOverrideChange={handleStyleOverrideChange("title")}
					/>
					<EditableTextDraftQuill
						className="video-description"
						value={t(`${namespace}:videos.${page_module.id}.description`, t(`${namespace}:videos.description`, description))}
						onChange={handleTextChange("description")}
						elementName={EContentColorOverrideNames.description}
						defaultColor={ColorKeys.bodyTextColor}
						styleOverrides={styling_overrides?.content?.description}
						onOverrideChange={handleStyleOverrideChange("description")}
						useNew={contentEditor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(page_module, template)}
					/>
				</div>

				{videos && selectedVideos.length ? (
					<div className="video-slider">
						<Suspense fallback="">
							<Slider
								{...sliderSettings}
								prevArrow={<PrevArrow />}
								nextArrow={<NextArrow />}
							>
								{selectedVideos.map((video: HomepageVideo, idx: number) => (
									<Video
										key={video.source}
										// hard code use_thumbnail so video thumbnail displayed in module is same as what was displayed in modal
										video={{ ...video, use_thumbnail: true }}
										template={template}
										multi={videos.length > 1}
										editVideo={(video) => editVideo(video, idx)}
										checkDeleteVideo={checkDeleteVideo}
									/>
								))}
							</Slider>
						</Suspense>
					</div>

				) : <LargeButton
					title="Add Video"
					onClick={openSelectVideos}
					style={{ width: "100%", marginTop: '20px' }}
					nonUpload
				/>
				}
			</div>


			<ModalComponent
				open={modalOpen}
				onRequestClose={closeModal}
				cancellable={true}
				closeable={false}
				title={modalTitle}
				footer={
					editingImage ? (
						<div className="image-edit-footer">
							<div className="image-edit-left">
								{workingVideo?.original_image && (
									<>
										<button
											className="small"
											onClick={pickNewPhoto}
										>
											<Icon
												name={ICONS.UPLOAD}
												size={10}
												color={COLORS.WHITE}
											/>{' '}
											Replace photo
										</button>
										<FileInput
											accept={['image/png', 'image/jpg', 'image/jpeg', 'image/gif']}
											type="file"
											style={{ display: 'none' }}
											ref={replaceImageInput}
											onChange={handleNewPhoto}
										/>
									</>
								)}
							</div>
							<div className="image-edit-right">
								<button
									onClick={cancelEditingImage}
									style={{ marginRight: 16 }}
								>
									Cancel
								</button>
								<button
									onClick={doneEditingImage}
									className="primary"
									disabled={uploading}
								>
									{uploading ? (
										<WaitingIndicator />
									) : (
										'Save & Continue'
									)}
								</button>
							</div>
						</div>
					) : (
						<div className="edit-video-footer">
							{(isSelectVideoView(addVideoType)) ? <div /> : (
								<div
									className="field-group switch"
									style={{ marginBottom: 16 }}
								>
									<Switch
										value={'use-thumb'}
										on={!!workingVideo?.use_thumbnail}
										onClick={handleSwitch}
									/>
									<label>Add custom thumbnail</label>
								</div>
							)}
							<div className="editing-buttons">
								<button
									onClick={handleCancel}
									style={{ marginRight: 16 }}
								>
									Cancel
								</button>
								<button
									onClick={doneAddingVideo}
									className="primary"
								>
									Done
								</button>
							</div>

						</div>
					)
				}
			>
				{!editingImage ? (
					<>
						{/* <TextInput
							onChange={handleUrlChange}
							label={'Video URL*'}
							placeholder={'Enter video url'}
							onBlur={checkUrl}
							defaultValue={workingVideo?.source ?? ''}
							valid={
								!inputError
									? Validation.normal
									: Validation.error
							}
						/>

						{inputError && <p className="error">{inputError}</p>}

						<div
							className="field-group switch"
							style={{ marginBottom: 16 }}
						>
							<Switch
								value={'use-thumb'}
								on={!!workingVideo?.use_thumbnail}
								onClick={handleSwitch}
							/>
							<label>Add custom thumbnail</label>
						</div> */}
						<div className="add-video-modal">
							{isSelectVideoView(addVideoType) && workingVideo?.use_thumbnail ? null : (
								<div className="video-type-select">
									<div className={classNames("video-type-selected", { open: selectingType })} onClick={() => setSelectingType(!selectingType)}>
										{selectedWorkingType}
										<span className="arrow"><Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={COLORS.WHITE} /></span>
									</div>
									<div className={classNames("video-type-selector", { open: selectingType })}>
										<div onClick={setSelectedType('uploaded')} className="select-option">
											<AddVideo />
										</div>
										<div onClick={setSelectedType('url')} className="select-option">
											<LinkVideo />
										</div>
										<div onClick={setSelectedType('embed')} className="select-option">
											<EmbedIframe />
										</div>
									</div>
								</div>
							)
							}
							{renderVideoSelect}
						</div>

						{workingVideo?.use_thumbnail && (
							<>
								{/* upload thumbnail if there isnt an original image, or if there isnt an image the original image is the default thumbnail */}
								{(!workingVideo.original_image || workingVideo.original_image.includes('thumbnail.jpg')) ? (
									<LargeButton
										title={'Upload Thumbnail Photo'}
										allowedFileTypes={[
											'image/jpeg',
											'image/png',
											'image/gif',
										]}
										subtitle={
											'Allowed filetypes are .jpg, .png, and .gif, and up to 10MB'
										}
										onFile={handleFile}
									/>
								) : (
									<div className="video-thumbnail-data">
										<div className="image-container">
											<img
												src={workingVideo.image}
												alt={workingVideo.image}
											/>
											<span>{imageName}</span>
										</div>

										<div className="image-actions">
											<button
												className="no-style"
												onClick={editImage}
											>
												<Icon
													name={ICONS.EDIT}
													size={16}
													color={COLORS.WHITE}
												/>
											</button>
											<button
												className="no-style"
												onClick={removeImage}
											>
												<Icon
													name={ICONS.TRASH}
													size={16}
													color={COLORS.WHITE}
												/>
											</button>
										</div>
									</div>
								)}
							</>
						)}
					</>
				) : (workingVideo ? (
					<ImageEditor
						originalImage={workingVideo?.original_image}
						onFinished={(file: File) => setResizedImage(file)}
						setEditing={handleEditingImage}
					/>
				) : null
				)}
			</ModalComponent>

			<ModalComponent
				title={'Remove Video?'}
				cancellable={true}
				closeable={false}
				open={!!confirmDelete}
				onRequestClose={closeConfirmModal}
			>
				<>
					<p>
						Are you sure you want to remove{' '}
						<strong>{confirmDelete?.source}</strong>?
					</p>

					<div
						style={{
							display: 'flex',
							justifyContent: 'flex-end',
							marginTop: 32,
						}}
					>
						<button onClick={closeConfirmModal} style={{ marginRight: 16 }}>Cancel</button>
						<button className="primary" onClick={removeVideo}>Remove</button>
					</div>
				</>
			</ModalComponent>
		</section>
	) : null;
});

EditorVideo.displayName = 'EditorVideo';

export default EditorVideo;

function AddVideo() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.ADD} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Add Video</label>
				<span>Add video from content gallery or upload .mp4 files up to 5 GB</span>
			</div>
		</div>
	);
}

function LinkVideo() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.PAPERCLIP} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Link video</label>
				<span>Use YouTube, Vimeo, Wistia, Mixcloud, Dailymotion, or Twitch link</span>
			</div>
		</div>
	);
}

function EmbedIframe() {
	return (
		<div className="video-selected-type">
			<div className="icon-gradient">
				<Icon name={ICONS.CODE} size={20} color={COLORS.BLACK} />
			</div>

			<div className="video-selected-info">
				<label>Embedded Iframe</label>
				<span>Copy & paste the code of embed HTML code</span>
			</div>
		</div>
	);
}

function isSelectVideoView(type: HomepageVideo['type']) {
	return type === 'uploaded' || type === 'recorded';
}